import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import { Badge, Button, Card, Modal, ModalFooter } from '@/components/common';
import { customerService, getErrorMessage } from '@/services';
import type { DuplicateGroup, DuplicateMember } from '@/services/customers';
import { formatDate } from '@/utils';

/**
 * Reviewed merging of duplicate contacts.
 *
 * Nothing on this screen merges by itself. The server only ever proposes
 * candidates; a human picks the survivor and confirms. That split is deliberate
 * — a merge moves policies between records and cannot be undone from the UI.
 */

const MATCH_LABEL: Record<string, { label: string; variant: 'error' | 'warning' }> = {
  exact: { label: 'Same name, email and phone', variant: 'error' },
  similar_name: { label: 'Same phone, similar name', variant: 'warning' },
};

function attachmentSummary(member: DuplicateMember): string {
  const { policies, quotes, claims, documents, leads } = member.counts;
  const parts = [
    policies && `${policies} ${policies === 1 ? 'policy' : 'policies'}`,
    quotes && `${quotes} ${quotes === 1 ? 'quote' : 'quotes'}`,
    claims && `${claims} ${claims === 1 ? 'claim' : 'claims'}`,
    documents && `${documents} ${documents === 1 ? 'document' : 'documents'}`,
    leads && `${leads} ${leads === 1 ? 'lead' : 'leads'}`,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'nothing attached';
}

export function DuplicatesTab() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ group: DuplicateGroup; survivor: DuplicateMember } | null>(
    null,
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['customer-duplicates'],
    queryFn: () => customerService.duplicates(),
  });

  const merge = useMutation({
    mutationFn: ({ survivorId, losingIds }: { survivorId: string; losingIds: string[] }) =>
      customerService.merge(survivorId, losingIds),
    onSuccess: (result) => {
      toast.success(
        `Merged ${result.merged_ids.length} ${
          result.merged_ids.length === 1 ? 'record' : 'records'
        } into the one you kept`,
      );
      queryClient.invalidateQueries({ queryKey: ['customer-duplicates'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setPending(null);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const groups = data?.groups ?? [];

  if (isLoading) {
    return (
      <Card>
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-48 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-24 rounded bg-gray-100 dark:bg-gray-800" />
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <p className="text-sm text-gray-600 dark:text-gray-300">{getErrorMessage(error)}</p>
      </Card>
    );
  }

  if (groups.length === 0) {
    return (
      <Card>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          No duplicate contacts found.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {groups.length} {groups.length === 1 ? 'group' : 'groups'} to review. Merging moves
        every policy, quote, claim, document and lead onto the record you keep, and cannot be
        undone from here.
      </p>

      {groups.map((group) => (
        // The wrapper carries the test id because the shared Card does not
        // forward arbitrary props, and widening it for one screen is worse.
        <div key={`${group.match}-${group.key}`} data-testid={`duplicate-group-${group.key}`}>
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {group.customers[0]?.full_name || 'Unnamed contact'}
            </h3>
            <Badge variant={MATCH_LABEL[group.match]?.variant ?? 'warning'} size="sm">
              {MATCH_LABEL[group.match]?.label ?? group.match}
            </Badge>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {group.customers.map((member) => (
              <div
                key={member.id}
                className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
              >
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {member.full_name}
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{member.email}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{member.phone}</p>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Added {member.created_at ? formatDate(member.created_at) : 'unknown'}
                </p>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  {attachmentSummary(member)}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 w-full"
                  onClick={() => setPending({ group, survivor: member })}
                >
                  Keep this record
                </Button>
              </div>
            ))}
          </div>
        </Card>
        </div>
      ))}

      <Modal
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        title="Merge these contacts?"
      >
        {pending && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Everything below moves onto{' '}
              <span className="font-semibold">{pending.survivor.full_name}</span> (
              {pending.survivor.email}). The other records are closed and kept only as a
              record of the merge.
            </p>
            <ul className="space-y-2">
              {pending.group.customers
                .filter((c) => c.id !== pending.survivor.id)
                .map((c) => (
                  <li
                    key={c.id}
                    className="rounded border border-gray-200 p-3 text-sm dark:border-gray-700"
                  >
                    <span className="font-medium">{c.full_name}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {' '}
                      — {attachmentSummary(c)}
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button
            isLoading={merge.isPending}
            onClick={() =>
              pending &&
              merge.mutate({
                survivorId: pending.survivor.id,
                losingIds: pending.group.customers
                  .filter((c) => c.id !== pending.survivor.id)
                  .map((c) => c.id),
              })
            }
          >
            Merge
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
